<template>
    <page :title="pageTitle" :full="true">
        <template slot="pageTitleActions">
            <div class="switcher-md">
                <div class="form-group">
                    <select class="form-control" v-model="ordering">
                        <option value="available_asc">{{ $t('listings_availableAsc') }}</option>
                        <option value="available_desc">{{ $t('listings_availableDesc') }}</option>
                        <option value="distance_asc">{{ $t('listings_distanceAsc') }}</option>
                        <option value="distance_desc">{{ $t('listings_distanceDesc') }}</option>
                        <option disabled value="rating">{{ $t('listings_rating') }}</option>
                    </select>
                </div>
            </div>
        </template>
        <div class="row">
            <div class="col-sm-4 col-md-3 sidebar-wrapper-col">
                <div class="sidebar">
                    <div class="filter">
                        <div class="form-group">
                            <gmap-autocomplete
                                    class="form-control"
                                    @place_changed="setPlace">
                            </gmap-autocomplete>
                            <div class="text-center text-muted my-2"> - {{ $t('listings_or') }} -</div>
                            <button class="btn btn-secondary btn-block" @click="geoLocate">
                                <i class="fa fa-location-arrow"></i> {{ $t('listings_getLoc') }}
                            </button>
                        </div>
                        <gmap-map
                                :center="center"
                                :zoom="10"
                                style="width: 100%;  height: 200px; margin-bottom: 10px;">
                            <gmap-marker
                                    :position="center"
                                    :draggable="true"
                                    @dragend="dragEnd"
                                    @mousedown="hideRadius = true"
                                    @dragstart="hideRadius = true"
                            ></gmap-marker>
                            <gmap-circle
                                    v-if="!hideRadius && distanceFromCenter != maxDistance"
                                    :center="center"
                                    :radius="distanceFromCenter * 1000"
                                    :options="{strokeWeight: 2, strokeColor: '#000000', strokeOpacity: 0.3, fillColor: '#FFFFFF', fillOpacity: 0}"
                            ></gmap-circle>
                        </gmap-map>
                        <div class="form-group">
                            <label>{{ $t('listings_maxDist') }}</label>
                            <select class="form-control" v-model="distanceFromCenter">
                                <option v-for="(distance, index) in distances" :key="index" :value="index == 0 ? maxDistance : distance">
                                    {{ distance }}
                                    {{ index == 0 ? '' : 'km' }}
                                </option>
                            </select>
                        </div>
                        <div v-for="(criteria, index) in searchCriteria" :key="index">
                            <h2>{{ criteria.category }}</h2>
                            <div class="form-group" v-for="(filter, element) in criteria.characteristiclist" :key="element">
                                <div v-if="filter.datatype === 'Numeric'">
                                    <label>{{ filter.name }}</label>
                                    <input type="number" min="0" class="form-control" v-model="queryParams[filter.queryParam]">
                                </div>
                                <div v-if="filter.datatype === 'Text'">
                                    <label>{{ filter.name }}</label>
                                    <input type="text" class="form-control" v-model="queryParams[filter.queryParam]">
                                </div>
                                <div v-if="filter.datatype === 'Bool'">
                                    <div class="checkbox">
                                        <label><input type="checkbox" v-model="queryParams[filter.queryParam]"> {{ filter.name }}</label>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="form-group-btn form-group-btn-placeholder-gap">
                            <button :disabled="$wait.is('listing.*')" type="submit" class="btn btn-primary btn-block" @click="getResults">
                                <i class="fa fa-spinner fa-spin" v-if="$wait.is('listing.*')"></i>
                                <span v-if="$wait.is('listing.criteria')">{{ $t('listings_loadingSearchCriteria') }}</span>
                                <span v-else-if="$wait.is('listing.search')">{{ $t('listings_searching') }}</span>
                                <span v-else>{{ $t('listings_search') }}</span>
                            </button>
                        </div>
                        <div class="form-group-btn form-group-btn-placeholder-gap">
                            <button :disabled="$wait.is('listing.*')" type="submit" class="btn btn-secondary btn-block" @click="resetFilter">
                                <i class="fa fa-spinner fa-spin" v-if="$wait.is('listing.*')"></i>
                                <span>Reset filter</span>
                            </button>
                        </div>

                    </div>
                </div>
            </div>
            <div id="scrollPoint" class="col-sm-8 col-md-9 grid-wrapper">
                <div class="content">
                    <div class="row mt-3 mt-md-0 mt-xl-0">
                        <campsite-grid-item
                                v-for="(campsite, index) in orderedCampsites"
                                :key="index"
                                :campsite="campsite"
                        ></campsite-grid-item>
                    </div>
                </div>
            </div>
        </div>
    </page>
</template>

<script>
import Page from './utilities/Page'
import CampsiteGridItem from './utilities/CampsiteGridItem'
import { mapGetters, mapMutations } from 'vuex'
import { API } from '../api/axios'
import { ContentLoader } from 'vue-content-loader'

export default {
  name: 'Listings',
  components: {Page, CampsiteGridItem, ContentLoader},
  data () {
    return {
      searchCriteria: null,
      campsites: [{}, {}, {}, {}, {}, {}, {}, {}],
      center: {lat: 52.0704978, lng: 4.3006999000000405},
      distances: ['-', 1, 2, 3, 4, 5, 10, 15, 20, 25, 50, 100],
      maxDistance: 40000,
      distanceFromCenter: 100,
      hideRadius: false,
      queryParams: {},
      ordering: 'available_asc',
    }
  },
  computed: {
    ...mapGetters(['language', 'savedQueryParams']),
    pageTitle () {
      return this.$t('main_listings')
    },
    orderedCampsites () {
      let campsites = this.campsites.slice()

      let property = 'availablepitches'
      let direction = -1

      let sortNumber = (a, b) => ((a[property] - b[property]) * direction)

      let distance = (lat1, lon1, lat2, lon2, unit = 'K') => {
        let radlat1 = Math.PI * lat1 / 180
        let radlat2 = Math.PI * lat2 / 180
        let theta = lon1 - lon2
        let radtheta = Math.PI * theta / 180
        let dist = Math.sin(radlat1) * Math.sin(radlat2) + Math.cos(radlat1) * Math.cos(radlat2) * Math.cos(radtheta)
        if (dist > 1) {
          dist = 1
        }
        dist = Math.acos(dist)
        dist = dist * 180 / Math.PI
        dist = dist * 60 * 1.1515
        if (unit === 'K') { dist = dist * 1.609344 }
        if (unit === 'N') { dist = dist * 0.8684 }
        return dist
      }

      let compareDistance = (a, b) => {
        let aDistance = distance(a.latitude, a.longitude, this.center.lat, this.center.lng)
        let bDistance = distance(b.latitude, b.longitude, this.center.lat, this.center.lng)
        return (aDistance - bDistance) * direction
      }

      let selectedFunction

      switch (this.ordering) {
        case 'available_asc':
          property = 'availablepitches'
          direction = 1
          selectedFunction = sortNumber
          break
        case 'available_desc':
          property = 'availablepitches'
          direction = -1
          selectedFunction = sortNumber
          break
        case 'distance_asc':
          direction = 1
          selectedFunction = compareDistance
          break
        case 'distance_desc':
          direction = -1
          selectedFunction = compareDistance
          break
      }

      return campsites.sort(selectedFunction)
    },
  },
  methods: {
    ...mapMutations(['setSavedQueryParams', 'resetSavedQueryParams']),
    setPlace (place) {
      this.center = {
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng(),
      }
    },
    dragEnd (place) {
      this.center = {
        lat: place.latLng.lat(),
        lng: place.latLng.lng(),
      }
      this.hideRadius = false
    },
    geoLocate () {
      navigator.geolocation.getCurrentPosition(position => {
        this.center = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        }
      })
    },
    resetFilter () {
      this.queryParams = {}
      this.resetSavedQueryParams()
    },
    getResults (scrollTo = true) {
      this.$router.push({name: 'Listings', params: {lang: this.language}, query: this.filteredQueryParams()})
      this.setSavedQueryParams(this.filteredQueryParams())
      this.$wait.start('listing.search')
      scrollTo && (this.campsites = [{}, {}, {}, {}, {}, {}, {}, {}])

      API.get('/search',
        {
          params: {
            languageid: this.language, // mandatory
            latitude: this.center.lat, // mandatory
            longitude: this.center.lng, // mandatory
            distance: this.distanceFromCenter, // mandatory
            ...this.filteredQueryParams(),
          },
        })
        .then(response => {
          this.campsites = response.data.campsitelist
        })
        .catch(error => {
          console.error(error)
        })
        .then(() => {
          this.$wait.end('listing.search')
          scrollTo && document.getElementById('scrollPoint')
            .scrollIntoView({behavior: 'smooth', block: 'start', inline: 'start'})
        })
    },
    filteredQueryParams () {
      return Object.keys(this.queryParams)
        .filter(key => this.queryParams[key])
        .reduce((obj, key) => {
          obj[key] = this.queryParams[key]
          return obj
        }, {})
    },
  },
  mounted () {
    // this.queryParams = Object.assign(this.$route.query)
    this.setSavedQueryParams(this.$route.query)
    this.$wait.start('listing.criteria')
    API.get('/search/criteria/' + this.language)
      .then(response => {
        response.data.map(criteria => {
          criteria.characteristiclist.map(filter => {
            this.queryParams[filter.queryParam] = null
          })
        })
        this.searchCriteria = response.data
      })
      .then(() => {
        this.$wait.end('listing.criteria')

        Object.keys(this.savedQueryParams).forEach(key => {
          this.queryParams[key] = this.savedQueryParams[key]
        })

        // this.queryParams = Object.assign(this.savedQueryParams)
        this.getResults(false)
      })
  },
}
</script>

<style scoped lang="scss">
    @media (max-width: 767.98px) {
        .image-thumbnail {
            margin: 20px 20px 0 0;
            height: 50px;
            width: unset;
        }
    }

    .grid-wrapper {
        min-height: 100px;
    }
</style>



// WEBPACK FOOTER //
// src/components/Listings.vue